<template>
	<v-data-table
		v-model="selected"
		:loading="loading"
		:items="items"
		:server-items-length="total"
		:options.sync="options"
		:headers="headers"
		:sort-by="state.sortBy"
		:sort-desc="state.sortDesc"
		:show-expand="!!expand"
		:single-expand="expand && expand === 'single'"
		:expanded.sync="expanded"
		:items-per-page="limit"
		:disable-pagination="!pagination"
		:hide-default-footer="!pagination"
		:single-select="select && select === 'single'"
		:show-select="!!select"
		:item-key="primaryKey"
		:dense="dense"
		:footer-props="{
			'items-per-page-options': [limit],
			'disable-items-per-page': true,
			'show-current-page': true,
			'show-first-last-page': true,
		}"
	>
		<template v-if="search" v-slot:top>
			<app-row>
				<app-cell cols="12" sm="3" offset-sm="9" class="pt-0 pb-0">
					<input-text
						v-model="searchText"
						append-icon="fa-search"
						placeholder="Search"
						clearable
						:autocomplete="false"
						@input="onSearch"
					/>
				</app-cell>
			</app-row>
		</template>
		<template v-for="header in headers" v-slot:[`item.${header.value}`]="{ item }">
			<slot :name="`${header.value}`" :item="item">
				{{ formatItem(header, item) }}
			</slot>
		</template>
		<template v-slot:[`item.data-table-expand`]="{ isExpanded, expand, item }">
			<app-button
				v-if="expandValidation(item)"
				:icon="isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"
				:color="isExpanded ? 'primary' : undefined"
				icon-only
				icon-button
				@click="expandItem(isExpanded, expand)"
			/>
		</template>
		<template v-slot:expanded-item="{ item, headers }">
			<slot name="expanded-item" :item="item" :headers="headers" />
		</template>
	</v-data-table>
</template>

<script>
import { debounce } from 'lodash'

import { dig } from '@/utils/collection'
import PaginationType from '@/types/Pagination'
import TableStateType from '@/types/TableState'

function toPaginationType(options) {
	const {
		sortDesc,
		page,
		itemsPerPage,
		sortBy,
	} = options

	const direction = sortDesc.length && sortDesc[0] ? '-' : ''
	return new PaginationType(
		page,
		itemsPerPage,
		sortBy.length ? `${direction}${sortBy[0]}` : null,
	)
}

export default {
	props: {
		items: Array,
		headers: Array,
		total: Number,
		limit: {
			type: Number,
			default: 25,
		},
		dynamic: {
			type: Boolean,
			default: true,
		},
		pagination: {
			type: Boolean,
			default: true,
		},
		select: {
			type: [Boolean, String],
			default: false,
		},
		expand: {
			type: [Boolean, String],
			default: false,
		},
		expandValidation: {
			type: Function,
			default: () => true,
		},
		primaryKey: {
			type: String,
			default: '_id',
		},
		refresh: {
			type: [Boolean, String],	// false, true, hard (for hard refresh)
			default: false,
		},
		search: Boolean,
		searchDebounce: {
			type: Number,
			default: 500,
		},
		sortBy: String,
		sortDesc: Boolean,
		initialPage: {
			type: [String, Number],
			default: 1,
		},
		initialState: {
			type: TableStateType,
			default: () => new TableStateType(),
		},
		dense: Boolean,
	},
	data() {
		return {
			loading: false,
			options: {},
			selected: [],
			expanded: [],
			searchText: '',
			init: false,
			unlockedOptions: false, // Unlock options handler for same page as initial page
		}
	},
	computed: {
		state() {
			return {
				page: this.initialState.page > 1
					? this.initialState.page : parseInt(this.initialPage || 1, 10),
				sortBy: this.initialState.sortBy || this.sortBy,
				sortDesc: typeof this.initialState.sortDesc !== 'undefined'
					? this.initialState.sortDesc : this.sortDesc,
			}
		},
	},
	created() {
		// Initialize and unlock options handler if static or having initial page to 1
		if (!this.dynamic || this.state.page === 1) {
			this.init = true
			this.unlockedOptions = true
		}
	},
	watch: {
		options: {
			handler() {
				if (!this.dynamic) { return }

				// Prevent trigger when initializing to another page than first page
				if (!this.unlockedOptions && this.options.page === this.state.page) {
					return
				}

				this.loading = true
				this.selected = []
				this.expanded = []

				// Alternate event with injection of initial page
				if (!this.init) {
					this.$emit('update', toPaginationType({
						...this.options,
						page: this.state.page,
					}))
					return
				// Unlock options for options handler with same page as initial page
				} else if (!this.unlockedOptions) {
					this.unlockedOptions = true
				}

				this.$emit('update', toPaginationType(this.options))
			},
			deep: true,
		},
		selected() {
			this.$emit('select', this.selected.map(item => item[this.primaryKey]))
		},
		expanded() {
			this.$emit('expand', this.expanded.map(item => item[this.primaryKey]))
		},
		refresh(value) {
			if (!value) { return }
			this.loading = true
			this.selected = []

			// Hard refresh
			if (value === 'hard') {
				this.expanded = []
				this.options.page = 1
			}
			this.$emit('update', toPaginationType(this.options))
			this.$emit('update:refresh', false)
		},
		items() {
			this.loading = false
		},
		total(count) {
			// Set page after total as it can't be set to proper page if total is 0 or -1
			if (!this.init) {
				this.options.page = this.state.page
				this.init = true
			}

			if (count === -1) {
				this.loading = true
				this.selected = []
				this.expanded = []
				this.options.page = 1
				this.$emit('update', toPaginationType(this.options))
			}
		},
	},
	methods: {
		formatItem(header, item) {
			const value = header.cb ? header.cb(item) : dig(item, header.value)
			if (header.format && this.$options.filters[header.format]) {
				return this.$options.filters[header.format](
					value,
					...(Array.isArray(header.formatArgs) ? header.formatArgs : []),
				)
			}
			return value
		},
		expandItem(isExpanded, expand) {
			expand(!isExpanded)
		},
		debounceSearch: debounce((self, text) => {
			self.$emit('search', text)
		}, 500),
		onSearch() {
			this.debounceSearch(this, this.searchText)
		},
	},
}
</script>

<style>
	#app .v-data-table {
		border-radius: 0;
	}

	.v-data-table-header th .v-data-table-header__icon {
		margin-left: 6px;
		margin-top: 4px;
	}

	.v-data-table-header th.desc .v-data-table-header__icon {
		margin-top: -8px;
	}
</style>
